import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Building2, MapPin, Briefcase, Upload, Download } from 'lucide-react';
import { useCustomers, useCreateCustomer, Customer } from '@/hooks/useCustomers';
import { useHasPermission } from '@/hooks/useAuth';
import { useBulkImportCustomers, getCustomersExportUrl, csvToCustomers } from '@/hooks/useBulkOperations';
import { BulkImportModal } from '@/components/common/BulkImportModal';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { MobileFilterDrawer, MobileFilterButton, FilterField } from '@/components/common/MobileFilterDrawer';
import { cn } from '@/lib/utils';

const CUSTOMER_IMPORT_COLUMNS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'segment', label: 'Segment' },
  { key: 'tier', label: 'Tier' },
  { key: 'region', label: 'Region' },
  { key: 'website', label: 'Website' },
  { key: 'primaryContactName', label: 'Contact Name' },
  { key: 'primaryContactEmail', label: 'Contact Email' },
  { key: 'primaryContactPhone', label: 'Contact Phone' },
  { key: 'notes', label: 'Notes' },
];

const STATUS_COLORS: Record<Customer['status'], string> = {
  prospect: 'badge-yellow',
  active: 'badge-green',
  inactive: 'badge-gray',
};

const STATUS_LABELS: Record<Customer['status'], string> = {
  prospect: 'Prospect',
  active: 'Active',
  inactive: 'Inactive',
};

const REGION_LABELS: Record<string, string> = {
  EMEA: 'EMEA',
  Americas: 'Americas',
  APAC: 'APAC',
};

export function Customers() {
  const navigate = useNavigate();
  const canCreate = useHasPermission('customers.create');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [statusFilter, setStatusFilter] = useState<Customer['status'] | ''>('');
  const [regionFilter, setRegionFilter] = useState<'EMEA' | 'Americas' | 'APAC' | ''>('');
  const [searchQuery, setSearchQuery] = useState('');

  const bulkImport = useBulkImportCustomers();

  const activeFilterCount = [statusFilter, regionFilter].filter(Boolean).length;

  const resetFilters = () => {
    setStatusFilter('');
    setRegionFilter('');
  };

  const { data, isLoading, error } = useCustomers({
    status: statusFilter || undefined,
    region: regionFilter || undefined,
    search: searchQuery || undefined,
    limit: 50,
  });

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 p-4 text-red-700">
        Failed to load customers. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-xtravirt-dark">Customers</h1>
          <p className="text-sm text-gray-600">
            {data?.customers && data.total ? (
              <>
                Showing {Math.min(data.customers.length, data.total)} of {data.total} customers
              </>
            ) : (
              '0 customers'
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Export/Import - hidden on small mobile */}
          <a
            href={getCustomersExportUrl()}
            className="btn-secondary hidden sm:flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            <span className="hidden md:inline">Export</span>
          </a>
          <button
            onClick={() => setShowImportModal(true)}
            className="btn-secondary hidden sm:flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            <span className="hidden md:inline">Import</span>
          </button>
          {canCreate && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              <span className="hidden sm:inline">Add Customer</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Search - always visible */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>

        {/* Mobile filter button */}
        <MobileFilterButton
          onClick={() => setShowFilterDrawer(true)}
          hasActiveFilters={activeFilterCount > 0}
          activeCount={activeFilterCount}
        />

        {/* Desktop filters */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as Customer['status'] | '')}
          className="input w-36 hidden md:block"
        >
          <option value="">All Status</option>
          {Object.entries(STATUS_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <select
          value={regionFilter}
          onChange={(e) => setRegionFilter(e.target.value as 'EMEA' | 'Americas' | 'APAC' | '')}
          className="input w-36 hidden md:block"
        >
          <option value="">All Regions</option>
          {Object.entries(REGION_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Stats - 2x2 on mobile, 4 cols on desktop */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <div className="stat-card group">
          <div className="stat-card-header">
            <span className="stat-card-label">Total Customers</span>
            <div className="stat-card-icon bg-xtravirt-navy/10 text-xtravirt-navy">
              <Building2 className="h-5 w-5" />
            </div>
          </div>
          <div className="stat-card-value">{data?.total || 0}</div>
        </div>
        <div className="stat-card group">
          <div className="stat-card-header">
            <span className="stat-card-label">Active</span>
            <div className="stat-card-icon bg-green-50 text-green-600">
              <Building2 className="h-5 w-5" />
            </div>
          </div>
          <div className="stat-card-value text-green-700">
            {data?.customers.filter((c) => c.status === 'active').length || 0}
          </div>
        </div>
        <div className="stat-card group">
          <div className="stat-card-header">
            <span className="stat-card-label">Prospects</span>
            <div className="stat-card-icon bg-yellow-50 text-yellow-600">
              <Building2 className="h-5 w-5" />
            </div>
          </div>
          <div className="stat-card-value text-yellow-700">
            {data?.customers.filter((c) => c.status === 'prospect').length || 0}
          </div>
        </div>
        <div className="stat-card group">
          <div className="stat-card-header">
            <span className="stat-card-label">Total Projects</span>
            <div className="stat-card-icon bg-blue-50 text-blue-600">
              <Briefcase className="h-5 w-5" />
            </div>
          </div>
          <div className="stat-card-value text-blue-700">
            {data?.customers.reduce((sum, c) => sum + c.projectsCount, 0) || 0}
          </div>
        </div>
      </div>

      {/* Customers List */}
      {data?.customers && data.customers.length > 0 ? (
        <>
          {/* Mobile: Card view */}
          <div className="space-y-3 md:hidden">
            {data.customers.map((customer) => (
              <div
                key={customer.id}
                onClick={() => navigate(`/customers/${customer.id}`)}
                className="mobile-card cursor-pointer"
              >
                <div className="flex items-start gap-3">
                  <div className="rounded-lg bg-gradient-to-br from-xtravirt-cyan to-xtravirt-navy p-2 flex-shrink-0">
                    <Building2 className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-xtravirt-dark">{customer.name}</p>
                        {customer.industry && (
                          <p className="text-xs text-gray-500 mt-0.5">{customer.industry}</p>
                        )}
                      </div>
                      <span className={cn('badge text-xs', STATUS_COLORS[customer.status])}>
                        {STATUS_LABELS[customer.status]}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      {customer.region && (
                        <span className="flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          {customer.region}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Briefcase className="h-3 w-3" />
                        {customer.projectsCount} projects
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: Table view */}
          <div className="hidden md:block card overflow-hidden !p-0">
            <table className="table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Region</th>
                  <th>Status</th>
                  <th>Projects</th>
                  <th>Tier</th>
                </tr>
              </thead>
              <tbody>
                {data.customers.map((customer) => (
                  <tr
                    key={customer.id}
                    onClick={() => navigate(`/customers/${customer.id}`)}
                    className="cursor-pointer"
                  >
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="rounded-lg bg-gradient-to-br from-xtravirt-cyan to-xtravirt-navy p-2">
                          <Building2 className="h-5 w-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-xtravirt-dark">
                            {customer.name}
                          </p>
                          {customer.industry && (
                            <p className="text-sm text-gray-500">{customer.industry}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {customer.region && (
                        <div className="flex items-center gap-1 text-sm text-gray-600">
                          <MapPin className="h-4 w-4" />
                          {customer.region}
                          {customer.country && ` - ${customer.country}`}
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={cn('badge', STATUS_COLORS[customer.status])}>
                        {STATUS_LABELS[customer.status]}
                      </span>
                    </td>
                    <td>
                      <div className="flex items-center gap-1 text-sm">
                        <Briefcase className="h-4 w-4 text-gray-400" />
                        <span>{customer.projectsCount}</span>
                      </div>
                    </td>
                    <td className="text-sm text-gray-600 capitalize">
                      {customer.tier || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="rounded-lg border-2 border-dashed border-gray-200 p-12 text-center bg-white">
          <Building2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No customers found</h3>
          <p className="mt-2 text-sm text-gray-600">
            {searchQuery || statusFilter || regionFilter
              ? 'Try adjusting your filters'
              : 'Get started by adding your first customer'}
          </p>
          {!searchQuery && !statusFilter && !regionFilter && canCreate && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary mt-4"
            >
              Add Customer
            </button>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateCustomerModal onClose={() => setShowCreateModal(false)} />
      )}

      {/* Import Modal */}
      <BulkImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Import Customers"
        columns={CUSTOMER_IMPORT_COLUMNS}
        onImport={async (data) => {
          return bulkImport.mutateAsync(data as unknown as Parameters<typeof bulkImport.mutateAsync>[0]);
        }}
        transformRow={(row, mapping) => {
          const customers = csvToCustomers([row], mapping);
          return (customers[0] || null) as unknown as Record<string, unknown>;
        }}
      />

      {/* Mobile Filter Drawer */}
      <MobileFilterDrawer
        isOpen={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        onReset={resetFilters}
        hasActiveFilters={activeFilterCount > 0}
      >
        <FilterField label="Status">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as Customer['status'] | '')}
            className="input w-full"
          >
            <option value="">All Status</option>
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </FilterField>

        <FilterField label="Region">
          <select
            value={regionFilter}
            onChange={(e) => setRegionFilter(e.target.value as 'EMEA' | 'Americas' | 'APAC' | '')}
            className="input w-full"
          >
            <option value="">All Regions</option>
            {Object.entries(REGION_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </FilterField>
      </MobileFilterDrawer>
    </div>
  );
}

function CreateCustomerModal({ onClose }: { onClose: () => void }) {
  const createCustomer = useCreateCustomer();
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    region: '' as 'EMEA' | 'Americas' | 'APAC' | '',
    country: '',
    status: 'active' as Customer['status'],
    tier: 'standard' as const,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createCustomer.mutateAsync({
        ...formData,
        region: formData.region || undefined,
      });
      onClose();
    } catch (error) {
      console.error('Failed to create customer:', error);
    }
  };

  return (
    <div className="dialog-overlay">
      <div className="dialog">
        <div className="dialog-header">
          <h2 className="dialog-title">Add New Customer</h2>
        </div>

        <form onSubmit={handleSubmit} className="dialog-body space-y-4">
          <div>
            <label className="label">Company Name *</label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input mt-1 w-full"
              placeholder="e.g., Acme Corporation"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Industry</label>
              <input
                type="text"
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                className="input mt-1 w-full"
                placeholder="e.g., Financial Services"
              />
            </div>
            <div>
              <label className="label">Region</label>
              <select
                value={formData.region}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    region: e.target.value as 'EMEA' | 'Americas' | 'APAC' | '',
                  })
                }
                className="input mt-1 w-full"
              >
                <option value="">Select region</option>
                <option value="EMEA">EMEA</option>
                <option value="Americas">Americas</option>
                <option value="APAC">APAC</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Country</label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="input mt-1 w-full"
                placeholder="e.g., United Kingdom"
              />
            </div>
            <div>
              <label className="label">Status</label>
              <select
                value={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value as Customer['status'],
                  })
                }
                className="input mt-1 w-full"
              >
                <option value="active">Active</option>
                <option value="prospect">Prospect</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </form>

        <div className="dialog-footer">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary"
            disabled={createCustomer.isPending}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="btn-primary"
            disabled={createCustomer.isPending}
          >
            {createCustomer.isPending ? 'Creating...' : 'Add Customer'}
          </button>
        </div>
      </div>
    </div>
  );
}
